// SubscriptionScreen.jsx
import React, { useState, useEffect } from "react";

const modulesData = [
  {
    name: "Telehealth",
    icon: "📹",
    subscribed: true,
    description:
      "Video consultations with HD quality, integrated calendar, multi-participant rooms.",
    price: "$50/mo",
    priceId: "price_1",
    deps: [],
  },
  {
    name: "Clinical",
    icon: "⚕️",
    subscribed: false,
    description:
      "Comprehensive clinical records, patient management dashboards, secure charting features.",
    price: "$30/mo",
    priceId: "price_2",
    deps: [],
  },
  {
    name: "Messaging",
    icon: "✉️",
    subscribed: true,
    description:
      "Secure, HIPAA-compliant messaging between patients and providers with file sharing.",
    price: "$20/mo",
    priceId: "price_3",
    deps: ["Telehealth"],
  },
  {
    name: "Engage",
    icon: "❤️",
    subscribed: false,
    description:
      "Automated reminders, satisfaction surveys, and engagement analytics.",
    price: "$40/mo",
    priceId: "price_4",
    deps: ["Messaging"],
  },
  {
    name: "Billing",
    icon: "💲",
    subscribed: true,
    description:
      "Insurance claims processing, superbill generation, payment reconciliation.",
    price: "$35/mo",
    priceId: "price_5",
    deps: [],
  },
  {
    name: "Client Subscription",
    icon: "💳",
    subscribed: false,
    description:
      "Create subscription plans for clients who pay service on a subscription and recurring basis.",
    price: "$25/mo",
    priceId: "price_6",
    deps: ["Billing"],
  },
  {
    name: "AI Health Co-Pilot",
    icon: "🤖",
    subscribed: true,
    description:
      "AI decision support, auto-scoring, and risk-stratification insights.",
    price: "$70/mo",
    priceId: "price_7",
    deps: [],
    isPremium: true,
  },
  {
    name: "E-Prescribing",
    icon: "💊",
    subscribed: false,
    description:
      "Electronic prescription routing, formulary checks, med history integration.",
    price: "$45/mo",
    priceId: "price_8",
    deps: ["Clinical"],
  },
  {
    name: "Voice Scribing",
    icon: "🎙️",
    subscribed: false,
    description:
      "Real-time voice-to-text scribing with medical-grade accuracy.",
    price: "$55/mo",
    priceId: "price_9",
    deps: ["Clinical"],
    isPremium: true,
  },
  {
    name: "Stripe Integration",
    icon: "💳",
    subscribed: false,
    description: "Enable credit-card payments & recurring billing via Stripe.",
    price: "$15/mo",
    priceId: "price_10",
    deps: ["Billing"],
  },
];

const SubscriptionScreen = () => {
  const [modules, setModules] = useState(modulesData);
  const [paymentHistory, setPaymentHistory] = useState(
    JSON.parse(localStorage.getItem("paymentHistory")) || []
  );
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [previouslyFocused, setPreviouslyFocused] = useState(null);
  const [notification, setNotification] = useState({
    message: "",
    type: "success",
    show: false,
  });
  const [showHistory, setShowHistory] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && showModal) {
        closeConfirm();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showModal]);

  // Show notification
  const showNotification = (message, type = "success") => {
    setNotification({ message, type, show: true });
    setTimeout(
      () => setNotification({ message: "", type: "success", show: false }),
      3000
    );
  };

  // Calculate total cost
  const calculateTotal = () => {
    return modules
      .filter((m) => m.subscribed)
      .reduce((sum, m) => sum + Number(m.price.replace(/[^0-9.]/g, "")), 0);
  };

  // Update summary
  const getSummary = () => {
    const activeCount = modules.filter((m) => m.subscribed).length;
    return {
      activeCount,
      inactiveCount: modules.length - activeCount,
      total: calculateTotal(),
    };
  };

  // Toggle subscription
  const toggleSub = (name, to) => {
    setIsLoading(true);
    setTimeout(() => {
      setModules((prev) =>
        prev.map((m) => (m.name === name ? { ...m, subscribed: to } : m))
      );
      setIsLoading(false);
      const statusRegion = document.getElementById("statusRegion");
      if (statusRegion) {
        statusRegion.textContent = `${name} module ${
          to ? "subscribed" : "unsubscribed"
        }`;
      }
      showNotification(
        `${name} module ${to ? "added to" : "removed from"} your subscription`
      );
    }, 300);
  };

  // Confirm action
  const askConfirm = (message, action) => {
    setModalMessage(message);
    setPendingAction(() => action);
    setPreviouslyFocused(document.activeElement);
    setShowModal(true);
    setTimeout(() => {
      const confirmButton = document.querySelector(
        ".confirm-buttons .btn_subscription.confirm"
      );
      if (confirmButton) confirmButton.focus();
    }, 0);
  };

  // Close confirmation modal
  const closeConfirm = () => {
    setShowModal(false);
    setPendingAction(null);
    if (previouslyFocused) previouslyFocused.focus();
  };

  // Run pending action
  const runPending = () => {
    if (pendingAction) {
      pendingAction();
      closeConfirm();
    }
  };

  const handleCheckout = async () => {
    const btn = document.getElementById("checkoutBtn");
    const originalHtml = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = 'Processing... <span class="spinner"></span>';

    try {
      const total = calculateTotal();
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const mockSuccess = Math.random() > 0.1;

      if (!mockSuccess) {
        throw new Error("Payment processing failed. Please try again.");
      }

      const payment = {
        date: new Date().toISOString(),
        amount: `$${total}`,
        status: "success",
        message: "Subscription payment processed",
        invoiceId: `INV-${Date.now().toString().slice(-6)}`,
      };

      const updatedHistory = [...paymentHistory, payment].slice(-50);
      setPaymentHistory(updatedHistory);
      localStorage.setItem("paymentHistory", JSON.stringify(updatedHistory));
      showNotification("Payment processed successfully!");
      btn.innerHTML = originalHtml;
      btn.disabled = false;
    } catch (error) {
      console.error("Checkout error:", error);
      const payment = {
        date: new Date().toISOString(),
        amount: `$${calculateTotal()}`,
        status: "failed",
        message: error.message,
        invoiceId: `INV-${Date.now().toString().slice(-6)}`,
      };
      const updatedHistory = [...paymentHistory, payment].slice(-50);
      setPaymentHistory(updatedHistory);
      localStorage.setItem("paymentHistory", JSON.stringify(updatedHistory));
      showNotification(error.message, "error");
      btn.disabled = false;
      btn.innerHTML = `Pay <span id="checkoutAmount">$${calculateTotal()}</span>/mo`;
    }
  };

  // Handle demo button click
  const handleDemoClick = (moduleName) => {
    askConfirm(`Start demo for ${moduleName}?`, () => {
      showNotification(`Demo for ${moduleName} launched`, "success");
    });
  };

  // Render payment history
  const renderPaymentHistory = () => {
    if (paymentHistory.length === 0) {
      return <p className="empty-message">No payment history available</p>;
    }

    return paymentHistory
      .slice()
      .reverse()
      .map((payment, index) => (
        <div key={index} className="payment-record">
          <div
            style={{
              alignItems: "flex-start",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <strong>{new Date(payment.date).toLocaleDateString()}</strong>
            <div className={`payment-${payment.status}`}>{payment.message}</div>
            <small>
              {payment.invoiceId ? `Invoice: ${payment.invoiceId}` : ""}
            </small>
          </div>
          <div>
            <strong>{payment.amount}</strong>
          </div>
        </div>
      ));
  };

  const { activeCount, inactiveCount, total } = getSummary();

  return (
    <>
      <main className="section-box_subscription" aria-labelledby="pageTitle">
        <h1 id="pageTitle">Subscription Modules</h1>
        <p className="summary_subscription" style={{ textAlign: "left" }}>
          Active Modules: {activeCount} / Inactive Modules: {inactiveCount}
        </p>
        <p style={{ textAlign: "left", fontWeight: "bold" }}>
          Total: ${total}/mo
        </p>
        <div className="checkout-container">
          <button
            id="checkoutBtn"
            className="btn_subscription subscribe"
            disabled={total === 0}
            onClick={handleCheckout}
          >
            Pay <span id="checkoutAmount">${total}</span>/mo
          </button>
          <button className="history-btn" onClick={() => setShowHistory(true)}>
            Payment History
          </button>
        </div>
        <div className={`module-container ${isLoading ? "loading" : ""}`}>
          {modules.map((module) => {
            const unmet = module.deps.filter(
              (d) => !modules.find((x) => x.name === d).subscribed
            );
            return (
              <article
                key={module.name}
                className={`module ${
                  module.subscribed ? "subscribed" : "unsubscribed"
                }`}
                tabIndex={0}
                aria-label={`${module.name} module - ${
                  module.subscribed ? "active" : "inactive"
                }`}
              >
                <div className="module-info">
                  <div className="module-icon" aria-hidden="true">
                    {module.icon}
                  </div>
                  <div>
                    <div className="module-title">
                      {module.name}
                      {module.isPremium && (
                        <span className="premium">Premium</span>
                      )}
                    </div>
                    <div className="module-description">
                      {module.description}
                    </div>
                  </div>
                </div>
                <div className="button-container_subscription">
                  {module.subscribed ? (
                    <button
                      className="btn_subscription cancel"
                      onClick={() =>
                        askConfirm(
                          `Cancel subscription to ${module.name}?`,
                          () => toggleSub(module.name, false)
                        )
                      }
                      aria-label={`Cancel ${module.name} subscription`}
                    >
                      Cancel
                    </button>
                  ) : unmet.length ? (
                    <>
                      <button
                        className="btn_subscription subscribe"
                        disabled
                        aria-label={`Subscribe to ${
                          module.name
                        } (requires ${unmet.join(", ")})`}
                      >
                        Subscribe
                      </button>
                      {/* <div className="tooltip">Requires {unmet.join(", ")}</div> */}
                    </>
                  ) : (
                    <button
                      className="btn_subscription subscribe"
                      onClick={() =>
                        askConfirm(`Subscribe to ${module.name}?`, () =>
                          toggleSub(module.name, true)
                        )
                      }
                      aria-label={`Subscribe to ${module.name}`}
                    >
                      Subscribe
                    </button>
                  )}
                  {!module.subscribed && module.isPremium && (
                    <button
                      className="btn_subscription demo"
                      onClick={() => handleDemoClick(module.name)}
                      aria-label={`Try demo of ${module.name}`}
                    >
                      Try Demo
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </main>

      <div
        className={`payment-history section-box_subscription ${
          showHistory ? "" : "hidden"
        }`}
        style={{ width: "100%" }}
      >
        <div className="history-header">
          <h2>Payment History</h2>
          <button
            className="close-history"
            aria-label="Close payment history"
            onClick={() => setShowHistory(false)}
          >
            ×
          </button>
        </div>
        <div className="history-container">{renderPaymentHistory()}</div>
      </div>

      <div
        id="statusRegion"
        role="status"
        aria-live="polite"
        className="sr-only"
      ></div>

      <div
        className={`confirm-modal ${showModal ? "active" : ""}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirmTitle"
      >
        <div className="confirm-content" role="document">
          <button
            className="close-icon"
            aria-label="Close"
            onClick={closeConfirm}
          >
            ×
          </button>
          <h2 id="confirmTitle" style={{ textAlign: "left" }}>
            {modalMessage}
          </h2>
          <p id="confirmMessage" style={{ textAlign: "left" }}>
            {modalMessage}
          </p>
          <div className="confirm-buttons">
            <button className="btn_subscription cancel" onClick={closeConfirm}>
              Cancel
            </button>
            <button className="btn_subscription confirm" onClick={runPending}>
              Confirm
            </button>
          </div>
        </div>
      </div>

      <div
        className={`notification ${notification.show ? "show" : ""}`}
        style={{
          backgroundColor:
            notification.type === "error" ? "#842029" : " #28a745",
        }}
      >
        {notification.message}
      </div>
    </>
  );
};

export default SubscriptionScreen;
